import { useQuery } from '@tanstack/react-query';
import {
  getOrganizationDashboardAlertsHistory,
  getOrganizationDashboardTopAlerts,
} from '@kirin/api-client/api/organizations';

import { apiClient } from '@/lib/api-client';
import { queryKeys } from './queryKeys';
import { cacheConfigForCollaborativeData } from './queryConfig';
import { retryHandlers } from './helpers';

/**
 * Hook to fetch organization dashboard alerts history
 *
 * Uses React Query for caching and automatic refetching.
 * Data is treated as collaborative, since multiple users can
 * generate alerts within the same organization.
 *
 * @param organizationId - Organization ID (required)
 * @param from - Start of the time range (ISO 8601 string)
 * @param to - End of the time range (ISO 8601 string)
 * @param enabled - Whether to enable the query
 * @param tz - IANA timezone for local-midnight bucket alignment (e.g. "America/New_York")
 */
export function useOrganizationDashboardAlerts(
  organizationId: string | undefined,
  from: string,
  to: string,
  enabled: boolean = true,
  tz?: string
) {
  const isEnabled =
    enabled &&
    !!organizationId &&
    typeof from === 'string' &&
    typeof to === 'string';

  return useQuery({
    queryKey: queryKeys.organizations.dashboardAlerts(organizationId ?? '', {
      from,
      to,
      tz,
    }),
    queryFn: () => {
      if (!organizationId) {
        throw new Error('organizationId is required');
      }
      return getOrganizationDashboardAlertsHistory(apiClient, organizationId, {
        from,
        to,
        tz,
      });
    },
    enabled: isEnabled,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}

/**
 * Hook to fetch organization dashboard top alerts summary.
 *
 * Returns aggregated alerts with distinct user counts per alert.
 *
 * @param organizationId - Organization ID (required)
 * @param from - Start of the time range (ISO 8601 string)
 * @param to - End of the time range (ISO 8601 string)
 * @param limit - Maximum number of alerts to return (default: 10)
 * @param enabled - Whether to enable the query
 */
export function useOrganizationDashboardTopAlerts(
  organizationId: string | undefined,
  from: string,
  to: string,
  limit: number = 10,
  enabled: boolean = true
) {
  const isEnabled =
    enabled &&
    !!organizationId &&
    typeof from === 'string' &&
    typeof to === 'string';

  return useQuery({
    queryKey: queryKeys.organizations.dashboardTopAlerts(organizationId ?? '', {
      from,
      to,
      limit,
    }),
    queryFn: () => {
      if (!organizationId) {
        throw new Error('organizationId is required');
      }
      return getOrganizationDashboardTopAlerts(apiClient, organizationId, {
        from,
        to,
        limit,
      });
    },
    enabled: isEnabled,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}
